import type { ComponentType } from 'react';

/** Block types the API may send (append-only on the api side). Unknown names are skipped. */
export type SiteBlockType =
  | 'ANNOUNCEMENT'
  | 'HEADER'
  | 'HERO'
  | 'SEARCH'
  | 'QUICK_ACTIONS'
  | 'PERSONAL_PANEL'
  | 'FEATURE_GRID'
  | 'SIGNUP_CTA'
  | 'TESTIMONIALS'
  | 'RICH_TEXT'
  | 'FOOTER'
  | 'LEGACY_HTML'
  | 'DIRECTORY'
  | 'FEATURED_LOCATIONS'
  | 'MONTHLY_PITCH'
  | 'EVENTS'
  | 'STATS'
  | 'PROMOTION'
  | 'HOW_IT_WORKS'
  | 'LOGO_WALL'
  | 'APP_DOWNLOAD'
  | 'FAQ'
  | 'LEAD_FORM';

export type SiteAudience = 'ANY' | 'SIGNED_IN' | 'SIGNED_OUT';

/** `FALLBACK` is client-only: the client composed the default page after a fetch failure. */
export type SitePageSourceView = 'PUBLISHED' | 'DEFAULT' | 'PREVIEW' | 'FALLBACK';

/** A section after normalisation: id/type present, audience resolved. `type` stays a string. */
export interface SiteSectionView {
  id: string;
  type: string;
  audience: SiteAudience;
  slots: Record<string, unknown>;
  /**
   * Server-computed data of a bound section (PB-8614). Present only when the api sent a plain
   * object; absent (no key at all) on every other section. Only the mappers read it.
   */
  data?: Record<string, unknown>;
}

/** Props every registered block receives. Blocks are dumb adapters. */
export interface SiteBlockProps {
  sectionId: string;
  slots: Record<string, unknown>;
  /** Bound data (Child J). Only the bound blocks read it, and only through their mappers. */
  data?: unknown;
  isSignedIn: boolean;
}

export type SiteBlockRegistry = Partial<Record<SiteBlockType, ComponentType<SiteBlockProps>>>;

/** The icon names a block may reference (api `Constants.SiteIcon`); each maps to one `Md*` glyph. */
export const SITE_ICON_NAMES = [
  'CAR',
  'CALENDAR',
  'CREDIT_CARD',
  'QR_CODE',
  'TICKET',
  'MAP_PIN',
  'BUILDING',
  'PHONE',
  'MAIL',
  'STAR',
  'SHIELD',
  'CLOCK',
  'EV_CHARGER',
  'PLANE',
  'HOTEL',
  'BRIEFCASE',
  'USER',
  'MOBILE',
  'CHECK',
  'GIFT',
] as const;
export type SiteIcon = (typeof SITE_ICON_NAMES)[number];

/** What a CTA / quick-action tile does (api `Constants.SiteAction`). */
export const SITE_ACTION_NAMES = [
  'BOOK',
  'MONTHLY',
  'PAY_BY_ZONE',
  'PAY_CITATION',
  'VALIDATE',
  'MY_ACCOUNT',
  'CONTACT_SALES',
  'URL',
] as const;
export type SiteAction = (typeof SITE_ACTION_NAMES)[number];

/**
 * A LINK slot that passed `resolveSiteLink`: a safe in-app path (a ROUTE, a white-label tab, or a
 * PAGE link's api-resolved `/pages/{slug}` path) or an https URL.
 */
export interface SiteLinkView {
  href: string;
  /** True for an absolute https URL (opens in a new tab with `noopener noreferrer`). */
  external: boolean;
}

export interface AnnouncementView {
  text: string;
  /** Null = no link, or one that failed `resolveSiteLink` (the text renders without an anchor). */
  link: SiteLinkView | null;
  dismissible: boolean;
}

export interface HeaderNavItemView {
  label: string;
  /** Null = bad target; the label renders as plain text. */
  link: SiteLinkView | null;
  hiddenSignedOut: boolean;
}

export interface HeaderView {
  navItems: HeaderNavItemView[];
  showLogin: boolean;
}

export type HeroMedia = 'THEME' | 'IMAGE' | 'VIDEO' | 'NONE';

/** A CTA or quick-action target: the action plus (for `URL`) the resolved link. */
export interface SiteActionTarget {
  action: SiteAction;
  /** Only meaningful for `URL`; null when the url slot is missing or unsafe. */
  link: SiteLinkView | null;
}

export interface HeroCtaView extends SiteActionTarget {
  label: string;
}

export interface AuthoredHeroView {
  headline?: string;
  subheadline?: string;
  rotatingWords: string[];
  media: HeroMedia;
  imageUrl?: string;
  imageAlt?: string;
  overlay: boolean;
  primaryCta: HeroCtaView | null;
  secondaryCta: HeroCtaView | null;
  showSearch: boolean;
}

export type SearchModeName = 'HOURLY' | 'MONTHLY';

export interface SearchView {
  placeholder?: string;
  /** Slot modes ∩ {HOURLY, MONTHLY}, in slot order, no duplicates. Empty = slot gave none usable. */
  modes: SearchModeName[];
  defaultMode?: SearchModeName;
  geolocate: boolean;
  modeLabels: Partial<Record<SearchModeName, string>>;
}

export interface QuickActionTileView extends SiteActionTarget {
  label: string;
  icon: SiteIcon | null;
}

export type FeatureGridColumns = 3 | 4 | 6;

export interface FeatureCardView {
  icon: SiteIcon | null;
  title: string;
  blurb?: string;
  link: SiteLinkView | null;
}

export interface FeatureGridView {
  heading?: string;
  intro?: string;
  columns: FeatureGridColumns;
  cards: FeatureCardView[];
}

export interface RichTextView {
  heading?: string;
  body?: string;
}

export type SocialNetwork = 'facebook' | 'instagram' | 'linkedin' | 'x' | 'youtube' | 'tiktok';

export interface FooterSocialView {
  network: SocialNetwork;
  href: string;
}

export interface FooterBadgeView {
  imageUrl: string;
  alt: string;
  link: SiteLinkView | null;
}

export type FooterLegalKind = 'PRIVACY' | 'TERMS' | 'ACCESSIBILITY' | 'ALPR_NOTICE';

export interface FooterLegalView {
  kind: FooterLegalKind;
  /** The operator's wording, if any; else the block uses the i18n default label. */
  label?: string;
  /** The operator's resolved link, or null (none given, or rejected). */
  link: SiteLinkView | null;
  /** True when the operator supplied a link that failed `resolveSiteLink`. */
  linkRejected: boolean;
}

export interface FooterView {
  address?: string;
  phone?: string;
  email?: string;
  hours?: string;
  socials: FooterSocialView[];
  badges: FooterBadgeView[];
  legal: FooterLegalView[];
}

// ---------------------------------------------------------------------------
// Published site navigation (PB-9032 / PB-9033): the view model of `GET /site/navigation`.
// ---------------------------------------------------------------------------

export type SiteNavKind = 'GROUP' | 'PAGE' | 'LINK';
export type SiteNavZone = 'LEFT' | 'CENTER' | 'RIGHT';

/** One navigation item's icon after validation: a known library glyph, or an https upload. */
export type SiteNavIconView =
  | { source: 'LIBRARY'; name: SiteIcon }
  | { source: 'UPLOAD'; url: string; width?: number; height?: number };

/** What an item does once the layout has resolved it for the current viewer. */
export interface SiteNavNodeView {
  id: string;
  kind: SiteNavKind;
  /** Visible label; absent for an icon-only item. */
  label?: string;
  /** The accessible name; falls back to `label`. Never empty. */
  a11yLabel: string;
  icon: SiteNavIconView | null;
  /** A resolved destination (PAGE, LINK or a navigable ACTION). Null for a GROUP or a button. */
  link: SiteLinkView | null;
  /** A button item (an ACTION that opens a pop-up). Mutually exclusive with `link`. */
  onActivate?: () => void;
  /** GROUP only: the Overview destination, when the api resolved one. */
  overview: SiteLinkView | null;
  /** GROUP only: the items of the group (never empty for a kept GROUP). */
  children: SiteNavNodeView[];
}

export interface SiteNavZonesView {
  left: SiteNavNodeView[];
  center: SiteNavNodeView[];
  right: SiteNavNodeView[];
}
